'use client'

import { useEffect, useRef, useState } from 'react'
import { Headphones, Pause, Play, Volume2, VolumeX } from 'lucide-react'
import Hls from 'hls.js'
import { useTranslation } from 'react-i18next'
import { resolveAssetPath } from '@/utils/basePath'

/** أخضر زاهٍ قريب من مرجع Figma / رحلة سكينة */
const RADIO_GREEN_TOP = '#00C97A'
const RADIO_GREEN_BOTTOM = '#00B36E'
const RADIO_ACCENT = '#00B36E'
/** أخضر داكن شبه أسود — زر التشغيل (مرجع Figma، ليس كحلي) */
const FREQUENCY_ICON = '/assets/frequency_icon.png'

/** تخطٍ مملوء — أسود صريح (لا يعتمد على لون النص فقط؛ يبقى أسود حتى مع disabled على البث المباشر) */
function RadioSkipBackIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <rect x="3.5" y="5.5" width="3.5" height="13" rx="0.75" className="fill-black dark:fill-gray-100" />
      <path d="M9 12l8.5-5.5v11L9 12z" className="fill-black dark:fill-gray-100" />
    </svg>
  )
}

function RadioSkipForwardIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <path d="M15 12L6.5 6.5v11L15 12z" className="fill-black dark:fill-gray-100" />
      <rect x="17" y="5.5" width="3.5" height="13" rx="0.75" className="fill-black dark:fill-gray-100" />
    </svg>
  )
}

type RelaxationRadioProps = {
  src: string
  title?: string
  reciter?: string
  forceLive?: boolean
}

export default function RelaxationRadio({
  src,
  forceLive = false,
}: RelaxationRadioProps) {
  const { t, i18n } = useTranslation()
  /** ترويسة البطاقة بالعربية دائماً كما في التصميم */
  const ar = i18n.getFixedT('ar', 'translation')
  const headerTitle = ar('home.radio.quranTitle', 'إذاعة القرآن الكريم')
  const headerReciterLine = `${ar('home.radio.reader', 'القارئ')}: ${ar('home.radio.reciter', 'الإذاعة الرسمية من القاهرة')}`

  const audioRef = useRef<HTMLAudioElement>(null)
  const hlsRef = useRef<Hls | null>(null)
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [isPlaying, setIsPlaying] = useState(false)
  const [volume, setVolume] = useState(0.2)
  const [isMuted, setIsMuted] = useState(false)
  const volumeBeforeMuteRef = useRef(0.2)
  const [duration, setDuration] = useState(0)
  const [isLive, setIsLive] = useState<boolean>(forceLive || false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const volumePercent = Math.round(volume * 100)
  const sliderPercent = isMuted ? 0 : volumePercent

  const toggleMute = () => {
    if (isMuted) {
      const restore =
        volumeBeforeMuteRef.current > 0 ? volumeBeforeMuteRef.current : 0.2
      setVolume(restore)
      setIsMuted(false)
    } else {
      if (volume > 0) volumeBeforeMuteRef.current = volume
      setIsMuted(true)
    }
  }

  useEffect(() => {
    const el = audioRef.current
    if (!el) return

    if (hlsRef.current) {
      hlsRef.current.destroy()
      hlsRef.current = null
    }

    setErrorMsg(null)
    setIsLive(forceLive)

    const isM3U8 = /\.m3u8($|\?)/i.test(src)
    if (isM3U8 && Hls.isSupported()) {
      const hls = new Hls({
        liveDurationInfinity: true,
        fragLoadingRetryDelay: 1500,
        manifestLoadingRetryDelay: 1500,
        enableWorker: true,
      })
      hlsRef.current = hls
      hls.attachMedia(el)
      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(src)
      })
      hls.on(Hls.Events.MANIFEST_PARSED, (_, data) => {
        const level = data.levels?.[0]
        const live = level?.details?.live ?? true
        setIsLive(forceLive || !!live)
        if (isPlaying) el.play().catch(() => setIsPlaying(false))
      })
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data.fatal) {
          setErrorMsg(t('home.radio.errorConnection', 'انقطاع في البث — محاولة إعادة الاتصال…'))
          hls.destroy()
          hlsRef.current = null
          scheduleRetry()
        }
      })
    } else {
      el.src = src
      if (isM3U8) setIsLive(true)
      if (isPlaying) el.play().catch(() => setIsPlaying(false))
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy()
        hlsRef.current = null
      }
      if (retryTimer.current) {
        clearTimeout(retryTimer.current)
        retryTimer.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src])

  useEffect(() => {
    const el = audioRef.current
    if (!el) return
    if (isPlaying) el.play().catch(() => setIsPlaying(false))
    else el.pause()
  }, [isPlaying])

  useEffect(() => {
    const el = audioRef.current
    if (!el) return
    el.volume = Math.max(0, Math.min(1, volume))
    el.muted = isMuted
  }, [volume, isMuted])

  useEffect(() => {
    const el = audioRef.current
    if (!el) return

    const onLoadedMeta = () => {
      const d = el.duration
      const liveDetected = !Number.isFinite(d) || d === 0
      setIsLive(forceLive || liveDetected)
      setDuration(Number.isFinite(d) ? d : 0)
    }
    const onEnded = () => setIsPlaying(false)
    const onError = () => {
      setErrorMsg(t('home.radio.errorPlayback', 'حدث خطأ في التشغيل — سيتم إعادة المحاولة…'))
      scheduleRetry()
    }
    el.addEventListener('loadedmetadata', onLoadedMeta)
    el.addEventListener('ended', onEnded)
    el.addEventListener('error', onError)

    if ('mediaSession' in navigator) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: headerTitle,
        artist: headerReciterLine,
        album: 'Sakeena Radio',
      })
      navigator.mediaSession.setActionHandler?.('play', () => setIsPlaying(true))
      navigator.mediaSession.setActionHandler?.('pause', () => setIsPlaying(false))
      navigator.mediaSession.setActionHandler?.('stop', () => setIsPlaying(false))
      navigator.mediaSession.setActionHandler?.('seekforward', () => skip(10))
      navigator.mediaSession.setActionHandler?.('seekbackward', () => skip(-10))
    }

    return () => {
      el.removeEventListener('loadedmetadata', onLoadedMeta)
      el.removeEventListener('ended', onEnded)
      el.removeEventListener('error', onError)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerTitle, headerReciterLine, forceLive])

  const scheduleRetry = () => {
    const el = audioRef.current
    if (!el) return
    if (retryTimer.current) clearTimeout(retryTimer.current)
    retryTimer.current = setTimeout(() => {
      setErrorMsg(null)
      if (hlsRef.current) {
        hlsRef.current.recoverMediaError?.()
      } else {
        const wasPlaying = isPlaying
        el.load()
        if (wasPlaying) el.play().catch(() => setIsPlaying(false))
      }
    }, 2000)
  }

  const skip = (delta: number) => {
    if (!audioRef.current || isLive) return
    audioRef.current.currentTime = Math.max(
      0,
      Math.min(duration || 0, audioRef.current.currentTime + delta),
    )
  }

  return (
    <section
      className="bg-[#ecf8f3] py-12 transition-colors dark:bg-gray-900 md:py-14"
      data-screen-reader-ignore="true"
    >
      <audio ref={audioRef} preload="metadata" crossOrigin="anonymous" data-screen-reader-ignore="true" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8 text-center">
          <h2 className="font-arabic text-2xl font-bold text-gray-900 dark:text-gray-100 md:text-[1.65rem]">
            {t('home.radio.heading', 'راديو سكينة')}
          </h2>
        </div>

        <div className="mx-auto w-full max-w-[26rem]">
          <div
            className="overflow-hidden rounded-2xl bg-white shadow-[0_4px_28px_-10px_rgba(0,80,50,0.12)] dark:shadow-black/25 ring-1 ring-gray-900/[0.04] dark:ring-white/10 dark:bg-gray-950"
            dir="rtl"
          >
            {/* —— Header أخضر —— */}
            <div
              className="relative px-4 pb-0 pt-3.5 md:px-5 md:pt-4"
              style={{
                background: `linear-gradient(180deg, ${RADIO_GREEN_TOP} 0%, ${RADIO_GREEN_BOTTOM} 100%)`,
              }}
            >
              {/* شارات: أعلى اليسار بصرياً */}
              <div className="absolute left-3 top-3 flex flex-wrap items-center gap-1.5 md:left-4 md:top-3.5">
                <span className="inline-flex items-center gap-1 rounded-full bg-black/25 px-2 py-0.5 text-[10px] font-medium text-white backdrop-blur-sm">
                  <Headphones className="h-3 w-3 shrink-0 opacity-95" strokeWidth={2} />
                  12.5K
                </span>
                {isLive && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white shadow-sm">
                    <span className="h-1.5 w-1.5 rounded-full bg-white" aria-hidden />
                    LIVE
                  </span>
                )}
              </div>

              <div className="pr-1 pt-7 text-right md:pr-2 md:pt-8">
                <h3 className="font-arabic text-base font-bold leading-snug text-gray-900 md:text-lg">
                  {headerTitle}
                </h3>
                <p className="mt-1 font-arabic text-[11px] font-normal leading-relaxed text-gray-900/85 md:text-xs">
                  {headerReciterLine}
                </p>
              </div>

              {/* عرض كامل لعرض الهيدر الأخضر (تعويض padding الأب) */}
              <div className="mt-3 -mx-4 w-[calc(100%+2rem)] md:mt-4 md:-mx-5 md:w-[calc(100%+2.5rem)]">
                <img
                  src={resolveAssetPath(FREQUENCY_ICON)}
                  alt=""
                  className="block h-10 w-full object-cover object-center opacity-[0.92] md:h-11"
                  loading="lazy"
                  decoding="async"
                  aria-hidden
                />
              </div>
            </div>

            {/* —— منطقة التحكم البيضاء —— لاصق بالهيدر الأخضر بدون فراغ */}
            <div className="bg-white px-4 pb-4 pt-0 dark:bg-gray-950 md:px-5 md:pb-4">
              <div
                className="flex items-center justify-center gap-2.5 pt-3 md:gap-3 md:pt-3.5"
                dir="ltr"
              >
                <button
                  type="button"
                  onClick={() => skip(-10)}
                  disabled={isLive}
                  className="rounded-full p-1 text-black transition-colors hover:bg-black/[0.06] disabled:pointer-events-none disabled:cursor-not-allowed disabled:!opacity-100 dark:text-gray-100 dark:hover:bg-white/10"
                  aria-label={t('home.radio.back10', 'رجوع 10 ثواني')}
                >
                  <RadioSkipBackIcon className="h-4 w-4" />
                </button>

                <button
                  type="button"
                  onClick={() => setIsPlaying((p) => !p)}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#0f2922] text-white transition-colors hover:bg-[#0c211c] dark:bg-[#1a3830] dark:hover:bg-[#23483e]"
                  aria-label={isPlaying ? t('accessibility.pause') : t('accessibility.play')}
                >
                  {isPlaying ? (
                    <Pause className="h-[11px] w-[11px]" fill="currentColor" />
                  ) : (
                    <Play className="ml-px h-[11px] w-[11px]" fill="currentColor" />
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => skip(10)}
                  disabled={isLive}
                  className="rounded-full p-1 text-black transition-colors hover:bg-black/[0.06] disabled:pointer-events-none disabled:cursor-not-allowed disabled:!opacity-100 dark:text-gray-100 dark:hover:bg-white/10"
                  aria-label={t('home.radio.forward10', 'تقديم 10 ثواني')}
                >
                  <RadioSkipForwardIcon className="h-4 w-4" />
                </button>
              </div>

              <div className="mx-auto mt-4 flex max-w-[17.5rem] items-center gap-3 md:max-w-xs" dir="ltr">
                <button
                  type="button"
                  onClick={toggleMute}
                  className="shrink-0 rounded p-0.5 text-gray-900 transition-colors hover:bg-gray-100 hover:opacity-90 dark:text-gray-200 dark:hover:bg-gray-800"
                  aria-label={
                    isMuted
                      ? t('home.radio.unmute', 'إلغاء الكتم')
                      : t('home.radio.mute', 'كتم الصوت')
                  }
                >
                  {isMuted ? (
                    <VolumeX className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
                  ) : (
                    <Volume2 className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
                  )}
                </button>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={sliderPercent}
                  onChange={(e) => {
                    const v = Number(e.target.value) / 100
                    setVolume(v)
                    setIsMuted(false)
                    if (v > 0) volumeBeforeMuteRef.current = v
                  }}
                  className="h-1 flex-1 cursor-pointer appearance-none rounded-full bg-gray-200 accent-[var(--radio-accent)] dark:bg-gray-700 dark:accent-[#5ddcb3]"
                  style={{ ['--radio-accent' as string]: RADIO_ACCENT }}
                  aria-label={t('radio.volume', 'مستوى الصوت')}
                />
                <span className="w-6 shrink-0 text-right font-arabic text-[11px] font-medium tabular-nums text-gray-600 dark:text-gray-400">
                  {sliderPercent}
                </span>
              </div>

              {errorMsg && (
                <p className="mt-3 text-center font-arabic text-[11px] text-red-600 dark:text-red-400">
                  {errorMsg}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
